"use client";

import { useEffect, useRef, useState } from "react";

type ChatAccountInfo = {
  id: string;
  username: string;
  planLabel: string;
  remainingChats: number;
  monthlyQuota: number;
  membershipStatus: "active" | "expired";
  membershipExpiresAt: string | null;
  quotaPeriodEnd: string | null;
};

function formatDate(value: string | null): string {
  if (!value) return "不限";
  return new Date(value).toLocaleDateString("zh-CN");
}

export function ChatAccountInfoButton({ user }: { user: ChatAccountInfo }) {
  const [open, setOpen] = useState(false);
  const [displayUser, setDisplayUser] = useState(user);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayUser(user);
  }, [user]);

  useEffect(() => {
    const onQuotaChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ remainingChats?: number }>).detail;
      if (typeof detail?.remainingChats === "number") {
        setDisplayUser((current) => ({ ...current, remainingChats: detail.remainingChats ?? current.remainingChats }));
      }
    };
    window.addEventListener("pi-web:quota-changed", onQuotaChanged);
    return () => window.removeEventListener("pi-web:quota-changed", onQuotaChanged);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const openMembershipPlans = () => {
    window.dispatchEvent(new CustomEvent("pi-web:open-membership-plans"));
    setOpen(false);
  };

  return (
    <div ref={ref} className="chat-account-info-wrap">
      <button
        className="chat-account-overlay-pill"
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={`${displayUser.username} | ${displayUser.planLabel} | 剩余 ${displayUser.remainingChats} / ${displayUser.monthlyQuota} 次 | ${displayUser.id}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="chat-account-overlay-text">
          <span className="chat-account-overlay-name">{displayUser.username}</span>
          <span className="chat-account-overlay-plan">{displayUser.planLabel}</span>
        </span>
        <span className="chat-account-overlay-quota">剩余 {displayUser.remainingChats}/{displayUser.monthlyQuota}</span>
      </button>
      {open && (
        <section className="chat-account-popover" aria-label="用户信息">
          <div className="chat-account-popover-head">
            <div>
              <h2>{displayUser.username}</h2>
              <p>{displayUser.planLabel} · {displayUser.membershipStatus === "expired" ? "已过期" : "生效中"}</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="关闭用户信息">×</button>
          </div>
          <div className="chat-account-popover-grid">
            <div>
              <span>剩余次数</span>
              <strong>{displayUser.remainingChats}/{displayUser.monthlyQuota}</strong>
            </div>
            <div>
              <span>会员到期</span>
              <strong>{formatDate(displayUser.membershipExpiresAt)}</strong>
            </div>
            <div>
              <span>次数周期</span>
              <strong>{formatDate(displayUser.quotaPeriodEnd)}</strong>
            </div>
            <div>
              <span>账号 ID</span>
              <strong title={displayUser.id}>{displayUser.id.slice(0, 8)}...</strong>
            </div>
          </div>
          <button className="chat-account-popover-primary" type="button" onClick={openMembershipPlans}>
            查看会员套餐
          </button>
        </section>
      )}
    </div>
  );
}
